import React, { forwardRef } from 'react';

import { Theme } from '@emotion/react';
import styled from '@emotion/styled';
import { Button } from '@mui/material';
import { paperClasses } from '@mui/material/Paper';
import MuiSnackbar, { snackbarClasses } from '@mui/material/Snackbar';
import {
  CustomContentProps,
  SnackbarContent,
  useSnackbar,
  VariantType,
} from 'notistack';

const getTextColor = (theme: Theme) => ({
  default: theme.color.onInverseSurface,
  success: theme.color.onGreen,
  error: theme.color.onRed,
  info: theme.color.onInverseSurface,
  warning: theme.color.onInverseSurface,
});

const getBackgroundColor = (theme: Theme) => ({
  default: theme.color.inverseSurface,
  success: theme.color.green,
  error: theme.color.red,
  info: theme.color.inverseSurface,
  warning: theme.color.inverseSurface,
});

const StyledSnackbar = styled(MuiSnackbar)<{
  variant: VariantType;
}>`
  display: flex;
  gap: ${({ theme }) => theme.spacing('1000')};

  & .${paperClasses.root} {
    border-radius: ${({ theme }) => theme.shape.corner['0500']};
    max-width: 375px;
    background-color: ${({ theme, variant = 'default' }) =>
      getBackgroundColor(theme)[variant]};
  }

  &.${snackbarClasses.root} {
    position: static;

    ${({ theme }) => theme.breakpoints.up('sm')} {
      position: fixed;
      top: ${({ theme }) => theme.spacing('0500')};
    }
  }
`;

const ActionsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing('0500')};
`;

const Message = styled.p<{
  variant: VariantType;
}>`
  font: ${({ theme }) => theme.typography.body.regular};
  font-size: ${({ theme }) => theme.size['0875']};
  line-height: ${({ theme }) => theme.typescale.lineHeight.extended};
  color: ${({ theme, variant = 'default' }) => getTextColor(theme)[variant]};
  text-transform: ${({ theme }) => theme.textTransform.capitalize};
`;

export interface ISnackbarProps
  extends Omit<
    CustomContentProps,
    | 'action'
    | 'persist'
    | 'style'
    | 'variant'
    | 'iconVariant'
    | 'hideIconVariant'
  > {
  action?: {
    label: React.ReactNode;
    onClick(): void;
  };
  severity: VariantType;
}

export const Snackbar = forwardRef<HTMLDivElement, ISnackbarProps>(
  (props, forwardedRef) => {
    const { closeSnackbar } = useSnackbar();

    return (
      <SnackbarContent ref={forwardedRef}>
        <StyledSnackbar
          open
          anchorOrigin={props.anchorOrigin}
          variant={props.severity}
          onClose={(_) => closeSnackbar(props.id)}
          action={
            props.action && (
              <ActionsContainer>
                <Button
                  onClick={props.action.onClick}
                  variant="text"
                  size="small"
                >
                  {props.action.label}
                </Button>
              </ActionsContainer>
            )
          }
          message={<Message variant={props.severity}>{props.message}</Message>}
        />
      </SnackbarContent>
    );
  },
);
